/**
 * UAT 2026-05-13 — Shared display helpers for the Phase 39 notification
 * payload shape (SlaWebhookPayload). Consumed by:
 *   - NeedsAttentionCard (Dashboard region 4, column 2)
 *   - NotificationsPage  (sidebar Manage → Notifications, full list)
 *
 * The payload shape is locked at `backend/src/services/slaNotificationDispatchService.ts:504`:
 *   { project: { id, name, ... }, tier?: { name, ... }, threshold?: { percentage, periodYearMonth }, ... }
 */
import { format } from 'date-fns';
import { cs } from 'date-fns/locale';
import type { Notification } from '../services/api/notifications';

export const KIND_LABEL: Record<Notification['kind'], string> = {
  'sla.warning': 'SLA warning',
  'sla.breach': 'SLA breach',
  'package.threshold_crossed': 'Hour Package threshold',
  'package.cap_blocked': 'Hour Package cap reached',
  'package.zero_rate_overage': 'Hour Package overage at 0 CZK',
  'package.overage_threshold_crossed': 'Overage pool threshold',
  'package.overage_approved': 'PM approved overage',
  'training.due': 'Training due soon',
  'training.overdue': 'Training overdue',
  // Quick 260806-ddg — Linear-style default task notifications.
  'task.assigned': 'Task assigned',
  'task.activity': 'Task activity',
  // Phase 87 — Claude bot usage weekly-window alert. Was rendering as the raw
  // `bot_usage.threshold_crossed` code on prod because the kind was missing
  // from the frontend union, which made these Records look complete to TS.
  'bot_usage.threshold_crossed': 'Bot usage limit',
  // Quick task 260914-pxp — a project's PM is notified when a human creates
  // an internal task in their project.
  'task.created': 'Task created',
  // MKS-209 — the monthly "you logged nothing on this project" reminder.
  nothing_logged: 'Nothing logged this month',
  // MKS-139 — the daily billing-schedule reminders.
  'billing_period.ending_soon': 'Billing period ending soon',
  'billing_period.phase_overdue': 'Billing phase overdue',
  'billing_period.ready_not_invoiced': 'Ready to invoice',
  'invoice.overdue': 'Invoice overdue',
};

export const KIND_TONE: Record<Notification['kind'], { bg: string; fg: string }> = {
  'sla.warning': { bg: '#FEF3C7', fg: '#92400E' },
  'sla.breach': { bg: '#FEE2E2', fg: '#991B1B' },
  'package.threshold_crossed': { bg: '#FEF3C7', fg: '#92400E' },
  'package.cap_blocked': { bg: '#FEE2E2', fg: '#991B1B' },
  // B2d (2026-05-15) — distinct rose-pink tone so admins do not confuse it
  // with threshold/cap kinds; the action required (add rate, re-generate) is
  // different and the badge color should call that out.
  'package.zero_rate_overage': { bg: '#FCE7F3', fg: '#9D174D' },
  // B2e (2026-05-16) — Wave 2 threshold on the approved overage pool. Same
  // visual family as package.threshold_crossed (amber) but with a slightly
  // deeper shade to signal "this is the second consumption bar".
  'package.overage_threshold_crossed': { bg: '#FED7AA', fg: '#9A3412' },
  // B2f (2026-05-16) — informational audit signal, distinct emerald palette
  // so admin/finance see at a glance "PM signed off something" (not an
  // alert in the warning/breach family).
  'package.overage_approved': { bg: '#D1FAE5', fg: '#065F46' },
  // Phase 64 — training reminders: amber for an approaching deadline, red for
  // an overdue one (matching the email tones).
  'training.due': { bg: '#FEF3C7', fg: '#92400E' },
  'training.overdue': { bg: '#FEE2E2', fg: '#991B1B' },
  // Quick 260806-ddg — neutral blue tones, distinct from the alert (amber/red)
  // and approval (emerald) families: task activity is informational, not a
  // warning.
  'task.assigned': { bg: '#DBEAFE', fg: '#1E40AF' },
  'task.activity': { bg: '#DBEAFE', fg: '#1E40AF' },
  // Phase 87 — amber, the same warning family as the package threshold kinds.
  // Deliberately one static tone even though the producer fires at both 80%
  // and 95% (BOT_USAGE_ALERT_THRESHOLDS): KIND_TONE is keyed by kind alone, and
  // the percentage is spelled out in the detail line instead.
  'bot_usage.threshold_crossed': { bg: '#FEF3C7', fg: '#92400E' },
  // Quick task 260914-pxp — same informational-blue family as task.assigned /
  // task.activity: this is a Linear-style default notification, not a warning.
  'task.created': { bg: '#DBEAFE', fg: '#1E40AF' },
  // MKS-209 — amber, the same "something needs your attention before the
  // month closes" family as the package thresholds. Not red: logging nothing
  // is a prompt, not a breach.
  nothing_logged: { bg: '#FEF3C7', fg: '#92400E' },
  // MKS-139 — the two heads-up kinds are amber (a prompt, with time left to
  // act); the two "this has already slipped" kinds are red, the same family as
  // sla.breach and package.cap_blocked.
  'billing_period.ending_soon': { bg: '#FEF3C7', fg: '#92400E' },
  'billing_period.phase_overdue': { bg: '#FEE2E2', fg: '#991B1B' },
  'billing_period.ready_not_invoiced': { bg: '#FEF3C7', fg: '#92400E' },
  'invoice.overdue': { bg: '#FEE2E2', fg: '#991B1B' },
};

export const KIND_TAG: Record<Notification['kind'], string> = {
  'sla.warning': 'SLA',
  'sla.breach': 'SLA',
  'package.threshold_crossed': 'Hour Pkg',
  'package.cap_blocked': 'Hour Pkg',
  'package.zero_rate_overage': 'Hour Pkg',
  'package.overage_threshold_crossed': 'Overage',
  'package.overage_approved': 'Approval',
  'training.due': 'Training',
  'training.overdue': 'Training',
  // Quick 260806-ddg — Linear-style default task notifications.
  'task.assigned': 'Task',
  'task.activity': 'Task',
  'bot_usage.threshold_crossed': 'Bot usage',
  'task.created': 'Task',
  nothing_logged: 'Timesheet',
  // MKS-139 — one shared tag: these four are all "the billing schedule needs
  // attention", and the label already says which one.
  'billing_period.ending_soon': 'Billing',
  'billing_period.phase_overdue': 'Billing',
  'billing_period.ready_not_invoiced': 'Invoicing',
  'invoice.overdue': 'Invoicing',
};

/**
 * MKS-556 — the kind label with the crossed threshold percentage appended, e.g.
 * "Hour Package threshold - 90 %".
 *
 * One time entry or import that crosses 70/90/100 at once used to produce
 * three rows reading "Hour Package threshold", all with the same timestamp and
 * nothing to tell them apart ("notifikace se multiplikuji", Corteco 2026-11-03).
 * The producer only ever sends the percentage as `threshold.packagePct`
 * (slaNotificationDispatchService buildPayload), which neither the label nor
 * the generic detail line below ever read.
 *
 * `withPercentage` is the resolved `mks_556_highest_threshold_only` flag: the
 * caller reads it with `useFlag`, since this is a plain function.
 */
export function notificationKindLabel(
  kind: Notification['kind'],
  payload: Record<string, unknown> | null | undefined,
  withPercentage: boolean,
): string {
  const label = KIND_LABEL[kind] ?? kind;
  if (
    !withPercentage ||
    (kind !== 'package.threshold_crossed' && kind !== 'package.overage_threshold_crossed')
  ) {
    return label;
  }
  const threshold = (payload as PayloadShape | null | undefined)?.threshold;
  // `percentage` is the fallback for any older row written before the payload
  // settled on `packagePct`.
  const pct = threshold?.packagePct ?? threshold?.percentage;
  return typeof pct === 'number' ? `${label} - ${pct} %` : label;
}

interface PayloadShape {
  event?: string;
  project?: { id?: number; name?: string };
  tier?: { name?: string } | null;
  threshold?: {
    percentage?: number;
    periodYearMonth?: string;
    zeroRateHours?: number;
    totalOverageHours?: number;
    packagePct?: number;
    overageConsumedHours?: number;
    approvedOverageHours?: number;
    approvedById?: number;
    approvalId?: number;
  };
}

export function describePayload(p: Record<string, unknown>): {
  projectName: string;
  projectId: number | null;
  /** Set only for task.* payloads — links should target the task, not the project (MKS-144 UAT). */
  taskId: number | null;
  detail: string;
  /**
   * Where the row should link when there is no project or task to point at.
   * Callers derive their href from taskId / projectId first and fall back to
   * this. Added for `bot_usage.threshold_crossed`, which is projectless and so
   * rendered as dead, unclickable text (prod, 2026-09-08).
   */
  href?: string;
} {
  // Quick 260806-ddg — watcher-event (task.activity) / task.assigned payloads
  // carry `taskName` + a flat `projectName`/`projectId` (no `project.*`
  // nesting like the SLA/package branches below). `detail` surfaces the
  // human `message` line built server-side (notificationService.ts D-04).
  // Quick task 260914-pxp — task.created's payload carries the same shape
  // (taskName/projectName/projectId/taskId/message), so it renders correctly
  // through this branch with no change needed here.
  if (typeof p.taskName === 'string') {
    const projectName = typeof p.projectName === 'string' ? p.projectName : 'Unknown project';
    const projectId = typeof p.projectId === 'number' ? p.projectId : null;
    const taskId = typeof p.taskId === 'number' ? p.taskId : null;
    const detail = typeof p.message === 'string' ? p.message : '';
    return { projectName, projectId, taskId, detail };
  }

  // Phase 87 — bot usage weekly-window threshold alert. Projectless: the
  // payload carries a bot connection + the affected user, so the SLA/package
  // fallthrough below rendered it as "Unknown project" with no detail and no
  // link (prod, 2026-09-08). Producer:
  // backend/src/services/botUsageAlertService.ts writeNotificationRow.
  //
  // Payload: { connectionId, userId, userDisplayName, window, threshold,
  //            utilizationPct, windowResetAt, isRobot }
  if (typeof p.window === 'string' && typeof p.threshold === 'number') {
    const isRobot = p.isRobot === true;
    const name = typeof p.userDisplayName === 'string' && p.userDisplayName.trim() !== ''
      ? p.userDisplayName
      : null;
    // The robot account has no personal name worth showing; a missing name on
    // a human is still better than "Unknown project", so fall back to the id.
    const who =
      name ?? (isRobot ? 'Robot account' : typeof p.userId === 'number' ? `User #${p.userId}` : 'Bot account');
    const headline = isRobot && name ? `${name} (robot)` : who;

    const parts: string[] = [];
    // `utilizationPct` is a FRACTION (0..1) — the producer compares
    // `utilizationPct * 100 < threshold`, and the Phase 87 header contract is
    // fraction-consumed. Rendering it raw would show "0.96%" instead of "96%".
    if (typeof p.utilizationPct === 'number') {
      parts.push(`${Math.round(p.utilizationPct * 100)}% used`);
    }
    // Only seven-day windows can raise this alert (the producer filters on
    // isSevenDayWindow), and the window may be model-qualified, e.g.
    // `seven_day_opus`. Say which limit in words, and name the model family
    // when the window carries one.
    const family = p.window.replace(/^(seven_day|7d)_?/, '');
    parts.push(family ? `7-day ${family} limit` : '7-day limit');
    parts.push(`alerts at ${p.threshold}%`);
    if (typeof p.windowResetAt === 'string') {
      const reset = new Date(p.windowResetAt);
      if (!Number.isNaN(reset.getTime())) {
        parts.push(`resets ${format(reset, 'd. M. HH:mm', { locale: cs })}`);
      }
    }
    return {
      projectName: headline,
      projectId: null,
      taskId: null,
      detail: parts.join(' • '),
      // The bot usage dashboard is the actionable destination. BotAdminPage
      // reads `?tab=` so this deep-links straight to the Usage tab.
      href: '/admin/bot?tab=usage',
    };
  }

  // Phase 64 — training reminder payloads carry a course (no project). Surface
  // the course title as the headline and the deadline as the detail line; the
  // kind pill (KIND_LABEL) already conveys due-soon vs overdue.
  if (typeof p.courseTitle === 'string') {
    const due = typeof p.nextDueAt === 'string' ? p.nextDueAt : null;
    const d = due ? new Date(due) : null;
    const detail = d && !Number.isNaN(d.getTime()) ? `due ${format(d, 'd. M. yyyy', { locale: cs })}` : '';
    return { projectName: p.courseTitle, projectId: null, taskId: null, detail };
  }

  // MKS-209 — the monthly "you logged nothing on this project" reminder.
  // Flat payload like the task.* branch above, but with `period` instead of
  // `taskName`: { projectId, projectName, period, periodLabel, message,
  // actionUrl }. Placed after the task branch, so a (hypothetical) payload
  // carrying both still renders as a task — `period` alone is the marker.
  // Producer: backend/src/services/nothingLoggedNotificationService.ts.
  if (typeof p.period === 'string' && typeof p.projectName === 'string') {
    const projectId = typeof p.projectId === 'number' ? p.projectId : null;
    const detail = typeof p.periodLabel === 'string' ? p.periodLabel : p.period;
    return {
      projectName: p.projectName,
      projectId,
      taskId: null,
      detail,
      // The timesheet is where the person acts on this, not the project page.
      href: typeof p.actionUrl === 'string' ? p.actionUrl : undefined,
    };
  }

  // MKS-139 — the four billing-schedule reminders. Flat payload, marked by
  // `subjectName` (the billing period's name, or the invoice's name/number):
  // { projectId, projectName, subjectName, amount, date, message, actionUrl,
  //   periodId? | invoiceId?, daysOverdue? | daysReady? }.
  // Producer: backend/src/services/billingNotificationService.ts.
  if (typeof p.subjectName === 'string') {
    const projectName =
      typeof p.projectName === 'string' && p.projectName.trim() !== ''
        ? p.projectName
        : // A multi-project invoice has no single project to headline, so the
          // invoice itself becomes the headline rather than "Unknown project".
          p.subjectName;
    const parts: string[] = [p.subjectName];
    if (typeof p.amount === 'string' && p.amount !== '') parts.push(p.amount);
    if (typeof p.daysOverdue === 'number') parts.push(`${p.daysOverdue} days overdue`);
    else if (typeof p.daysReady === 'number') parts.push(`ready ${p.daysReady} days`);
    else if (typeof p.date === 'string') parts.push(p.date);
    return {
      projectName,
      // Deliberately null even when the payload carries a project id: callers
      // derive their href from projectId FIRST, and for three of these four
      // kinds the project page is the wrong destination (the invoice, the
      // invoicing queue). Returning null routes every one of them through
      // `href` below, which is the destination the producer actually chose.
      projectId: null,
      taskId: null,
      detail: parts.join(' • '),
      // The billing section / the invoice / the invoicing queue — whichever
      // the producer decided is where this row is acted on.
      href: typeof p.actionUrl === 'string' ? p.actionUrl : undefined,
    };
  }

  const payload = p as PayloadShape;
  const projectName = payload.project?.name ?? 'Unknown project';
  const projectId = typeof payload.project?.id === 'number' ? payload.project.id : null;
  const pct = payload.threshold?.percentage;
  const tier = payload.tier?.name;
  const period = payload.threshold?.periodYearMonth;
  // B2d (2026-05-15) — zero-rate overage notifications carry hours instead of
  // a percentage, so the detail line surfaces "X / Y h • YYYY-MM" for the PM.
  if (payload.event === 'package.zero_rate_overage') {
    const zr = payload.threshold?.zeroRateHours;
    const total = payload.threshold?.totalOverageHours;
    const parts: string[] = [];
    if (zr != null && total != null) parts.push(`${zr.toFixed(1)} / ${total.toFixed(1)} h @ 0 CZK`);
    else if (zr != null) parts.push(`${zr.toFixed(1)} h @ 0 CZK`);
    if (period) parts.push(period);
    return { projectName, projectId, taskId: null, detail: parts.join(' • ') };
  }
  // B2e (2026-05-16) — overage pool threshold detail: "X% • A / B h overage".
  if (payload.event === 'package.overage_threshold_crossed') {
    const pPct = payload.threshold?.packagePct;
    const oc = payload.threshold?.overageConsumedHours;
    const ao = payload.threshold?.approvedOverageHours;
    const parts: string[] = [];
    if (pPct != null) parts.push(`${pPct}%`);
    if (oc != null && ao != null) parts.push(`${oc.toFixed(1)} / ${ao.toFixed(1)} h overage`);
    if (period) parts.push(period);
    return { projectName, projectId, taskId: null, detail: parts.join(' • ') };
  }
  // B2f (2026-05-16) — PM overage approval detail: "X h approved • YYYY-MM".
  if (payload.event === 'package.overage_approved') {
    const ao = payload.threshold?.approvedOverageHours;
    const parts: string[] = [];
    if (ao != null) parts.push(`${ao.toFixed(1)} h approved`);
    if (period) parts.push(period);
    return { projectName, projectId, taskId: null, detail: parts.join(' • ') };
  }
  let detail = '';
  if (tier && pct != null) {
    detail = `${tier} • ${pct}%`;
  } else if (tier) {
    detail = tier;
  } else if (pct != null) {
    detail = `${pct}%`;
  }
  return { projectName, projectId, taskId: null, detail };
}

export function currentMonthIsoRange(): { from: string; to: string } {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const first = new Date(y, m, 1);
  const last = new Date(y, m + 1, 0);
  const iso = (d: Date) => d.toISOString().slice(0, 10);
  return { from: iso(first), to: iso(last) };
}

export function timesheetReportHref(projectId: number): string {
  const r = currentMonthIsoRange();
  const params = new URLSearchParams({
    projectIds: String(projectId),
    from: r.from,
    to: r.to,
  });
  return `/reports/project/detailed?${params.toString()}`;
}
